import { forwardRef } from "react";
import {
  InputLabel,
  MenuItem,
  FormControl,
  Select as MuiSelect,
  FormHelperText,
  Box,
  Chip,
  OutlinedInput,
} from "@mui/material";

import { useSelectController } from "./controller";
import { SelectProps } from "./types";

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      options,
      fullWidth = true,
      required,
      value,
      error,
      helperText,
      multiple = false,
      onChange,
      ...rest
    },
    ref
  ) => {
    const { uniqueId, handleChange } = useSelectController({
      onChange,
      multiple,
      options,
    });
    const normalizeItem = (item: unknown): string | number => {
      if (item && typeof item === "object" && "value" in (item as any)) {
        return (item as any).value as string | number;
      }
      return item as string | number;
    };
    return (
      <FormControl fullWidth={fullWidth} required={required} error={!!error}>
        <InputLabel id={uniqueId}>{label}</InputLabel>

        <MuiSelect
          labelId={uniqueId}
          label={label}
          multiple={multiple}
          value={(() => {
            if (multiple) {
              if (Array.isArray(value)) return value.map(normalizeItem);
              if (value === undefined || value === null || value === "") return [];
              return [normalizeItem(value)];
            }
            if (Array.isArray(value)) return value.length ? normalizeItem(value[0]) : "";
            return (value && typeof value === "object" && "value" in (value as any))
              ? (value as any).value as string | number
              : (value ?? "") as string | number;
          })()}
          inputRef={ref}
          variant="outlined"
          onChange={handleChange}
          input={
            <OutlinedInput
              sx={{
                height: "45px",
                fontSize: 14,
                "& .MuiSelect-select": {
                  padding: "8px 12px",
                },
              }}
              label={label}
            />
          }
          renderValue={
            multiple
              ? (selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {(selected as Array<string | number>).map((val) => (
                      <Chip
                        key={val}
                        label={
                          options.find((opt) => opt.value === val)?.label || String(val)
                        }
                      />
                    ))}
                  </Box>
                )
              : undefined
          }
          {...rest}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </MuiSelect>

        {error && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }
);

Select.displayName = "Select";
